import Button from 'core-system/Button/Button'
import Table from 'core-system/Table/Table'
import { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text/Text'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { OneTimeRewardPayload } from 'redux/incentives/incentivesTypes'
import EmployeeService from 'redux/employee/employeeService'
import { SelectItemProps } from 'core-system/Dropdown'
import { NumberFormatter, NumberInput } from 'core-system/Input/NumberInput'
import TextField from 'core-system/TextField/TextField'
import { Add as AddIcon, Close as CloseIcon } from 'core-system/Icons/Actions'
import FormattingUtils from 'shared/FormattingUtils'
import { useDispatch } from 'react-redux'
import { incentivesActions } from 'redux/incentives/incentivesSlice'
import SegmentService from 'redux/config/services/SegmentService'
import AlertSuccessModal from 'core-system/Modal/AlertSuccessModal'
import SearchableDropdown from 'core-system/Dropdown/SearchableDropdown'

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 2rem;
`

const HeaderButtons = styled.div`
  display: flex;
  flex-direction: row;
`

const HoverableAddIcon = styled(AddIcon)`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
    background-color: ${(props) => props.theme.palette.primary.pink500};
    border-radius: 0.5rem;
  }
`

const HoverableCloseIcon = styled(CloseIcon)`
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
    background-color: ${(props) => props.theme.palette.primary.pink500};
    border-radius: 0.5rem;
  }
`

const getAddOtrTableRows = (
  otrsToAdd: OneTimeRewardPayload[],
  employeeNames: Record<string, string>,
  handleOtrDelete: (idx: number) => void
) => {
  return otrsToAdd.map((otr, idx) => {
    return (
      <TableRow key={idx}>
        <TableRowCell>{employeeNames[otr.employeeId]}</TableRowCell>
        <TableRowCell>{FormattingUtils.formatDollar(otr.amount)}</TableRowCell>
        <TableRowCell>{otr.memo}</TableRowCell>
        <TableRowCell>
          <HoverableCloseIcon
            width={24}
            height={24}
            onClick={() => handleOtrDelete(idx)}
          />
        </TableRowCell>
      </TableRow>
    )
  })
}

const getOtrAlertMessage = (currentOtrAdding: OneTimeRewardPayload) => {
  const invalidFields = []
  if (!currentOtrAdding.employeeId) invalidFields.push('Commuter')
  if (!currentOtrAdding.amount) invalidFields.push('Amount')
  if (!currentOtrAdding.memo || currentOtrAdding.memo === '')
    invalidFields.push('Description')
  return `Please fill in the following fields: ${invalidFields.join(', ')}`
}

interface AddOTRViewProps {
  setShowAddOTRView: (flag: boolean) => void
}

const AddOTRView = React.memo((props: AddOTRViewProps) => {
  const { setShowAddOTRView } = props

  const dispatch = useDispatch()

  const [commuterDropdownItems, setCommuterDropdownItems] = useState<
    { id: string; text: string }[]
  >([])
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>({})
  const searchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleEmployeeSearch = (value: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (value.trim().length < 1) {
      setCommuterDropdownItems([])
      return
    }
    searchTimer.current = setTimeout(() => {
      EmployeeService.searchEmployees(value.trim()).then((response) => {
        setCommuterDropdownItems(
          (response.data || []).map((employee: { id: string; name: string }) => ({
            id: employee.id,
            text: employee.name,
          }))
        )
      })
    }, 250)
  }

  const [otrsToAdd, setOtrsToAdd] = useState<OneTimeRewardPayload[]>([])
  const [currentOtrAdding, setCurrentOtrAdding] =
    useState<OneTimeRewardPayload>({
      employeeId: null,
      amount: 0,
      memo: '',
    })

  const [showAlertModal, setShowAlertModal] = useState(false)

  const currentOtrAddingInvalid = useMemo(() => {
    return !currentOtrAdding.employeeId ||
      !currentOtrAdding.amount ||
      !currentOtrAdding.memo ||
      currentOtrAdding.memo.length === 0
      ? true
      : false
  }, [currentOtrAdding])

  const setCommuter = (commuter: SelectItemProps) => {
    setCurrentOtrAdding({
      ...currentOtrAdding,
      employeeId: commuter ? commuter.id : null,
    })
    if (commuter?.id && commuter.text) {
      setEmployeeNames((prev) => ({ ...prev, [commuter.id]: commuter.text }))
    }
  }

  const clearCurrentOtrToAdd = () => {
    setCurrentOtrAdding({
      employeeId: null,
      amount: 0,
      memo: '',
    })
  }

  const handleCancel = () => {
    setShowAddOTRView(false)
    setOtrsToAdd([])
    clearCurrentOtrToAdd()

    SegmentService.track('cancel-bulk-add-otrs')
  }

  const handleCreateAdditionalOtr = () => {
    if (currentOtrAddingInvalid) {
      setShowAlertModal(true)
    } else {
      setOtrsToAdd([
        ...otrsToAdd,
        { ...currentOtrAdding, amount: currentOtrAdding.amount * 100 },
      ])
      clearCurrentOtrToAdd()
    }
  }

  const handleAddAllOtrs = () => {
    const rewardedOtrs = otrsToAdd

    dispatch(incentivesActions.bulkCreateOneTimeRewards(otrsToAdd))
    setShowAddOTRView(false)
    setOtrsToAdd([])
    clearCurrentOtrToAdd()

    SegmentService.track('bulk-add-otrs', {
      rewarded: rewardedOtrs,
    })
  }

  const handleOtrDelete = useCallback(
    (idx: number) => {
      const otrsToAddCopy = [...otrsToAdd]
      if (otrsToAdd.length > 1) {
        otrsToAddCopy.splice(idx, 1)
        setOtrsToAdd(otrsToAddCopy)
      } else {
        setOtrsToAdd([])
      }
    },
    [otrsToAdd]
  )

  const addOtrsTableRows = useMemo(() => {
    return getAddOtrTableRows(otrsToAdd, employeeNames, handleOtrDelete)
  }, [otrsToAdd, employeeNames, handleOtrDelete])

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Commuter'),
    HeaderCell('Amount'),
    HeaderCell('Description'),
    HeaderCell(''),
  ]

  return (
    <>
      <AlertSuccessModal
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        type='alert'
        headerText='Cannot Add One Time Reward'
        message={getOtrAlertMessage(currentOtrAdding)}
      />
      <HeaderContainer>
        <Text variant='h2'>Add One Time Rewards</Text>
        <HeaderButtons>
          <Button onClick={() => handleCancel()} variant='secondary'>
            Cancel
          </Button>
          <Button
            onClick={handleAddAllOtrs}
            disabled={otrsToAdd.length === 0}
            marginLeft='1rem'
          >
            Distribute Rewards
          </Button>
        </HeaderButtons>
      </HeaderContainer>
      <Table
        maxRows={10}
        headerCells={headerCells}
        rows={[
          ...addOtrsTableRows,
          <TableRow>
            <TableRowCell style={{ width: 300 }}>
              <SearchableDropdown
                options={commuterDropdownItems}
                itemCallback={setCommuter}
                onSearchChange={handleEmployeeSearch}
              />
            </TableRowCell>
            <TableRowCell>
              <NumberInput
                onValueChange={(value: number) =>
                  setCurrentOtrAdding({
                    ...currentOtrAdding,
                    amount: value,
                  })
                }
                value={currentOtrAdding.amount}
                height='2.75rem'
              >
                <NumberFormatter fixedDecimalScale />
              </NumberInput>
            </TableRowCell>
            <TableRowCell style={{ width: 300 }}>
              <TextField
                value={currentOtrAdding.memo}
                onChange={(e) =>
                  setCurrentOtrAdding({
                    ...currentOtrAdding,
                    memo: e.target.value,
                  })
                }
                maxLength={256}
                height='2.75rem'
              />
            </TableRowCell>
            <TableRowCell>
              <HoverableAddIcon
                width={32}
                height={32}
                onClick={handleCreateAdditionalOtr}
              />
            </TableRowCell>
          </TableRow>,
        ]}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  AddOTRView.displayName = 'AddOTRView'
}

export default AddOTRView
